import React from 'react'
import PropTypes from 'prop-types'
import { makeStyles } from '@material-ui/core'
import { formatDuration } from '../../../../utils/formatters'
import Icon from '../../../../components/atoms/Icon'

const useStyles = makeStyles((theme) => ({
  stepList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px'
  },
  step: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '8px 0'
  },
  stepConnector: {
    position: 'absolute',
    left: '10px',
    top: '28px',
    bottom: '-20px',
    width: '2px',
    backgroundColor: 'var(--color-neutral-600)',
    zIndex: 0
  },
  stepIconWrapper: {
    position: 'relative',
    zIndex: 1
  },
  stepIcon: {
    width: '12px',
    height: '12px',
    borderRadius: 'var(--border-radius-round)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '12px',
    fontWeight: 600,
    transition: 'all 0.3s ease',
    backgroundColor: 'var(--surface-bg-color-d2)',
    border: '2px solid var(--color-neutral-600)'
  },
  stepCompleted: {
    backgroundColor: 'var(--color-success-500)',
    borderColor: 'var(--color-success-500)',
    color: 'var(--color-contrast-success-500)',
    '& $stepIcon': {
      fontSize: '16px'
    }
  },
  stepActive: {
    backgroundColor: 'var(--color-primary-600)',
    borderColor: 'var(--color-primary-600)',
    color: 'white',
    animation: '$pulse 2s ease-in-out infinite'
  },
  stepPending: {
    backgroundColor: 'var(--surface-bg-color-d2)',
    borderColor: 'var(--color-neutral-600)',
    color: 'var(--color-neutral-400)'
  },
  stepContent: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between'
  },
  stepName: {
    fontSize: 'var(--font-size-xs)',
    fontWeight: 500,
    color: 'var(--text-color-main)'
  },
  stepNameActive: {
    color: 'var(--color-primary-500)',
    fontWeight: 600
  },
  stepNameCompleted: {
    color: 'var(--color-success-500)'
  },
  stepDuration: {
    fontSize: 'var(--font-size-xs)',
    color: 'var(--color-neutral-400)'
  },
  activeIndicator: {
    display: 'inline-block',
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    backgroundColor: 'var(--color-primary-500)',
    marginLeft: '8px',
    animation: '$blink 1.5s ease-in-out infinite'
  },
  loadingIcon: {
    animation: '$rotate 1s linear infinite'
  },
  '@keyframes pulse': {
    '0%': {
      transform: 'scale(1)',
      boxShadow: '0 0 0 0 rgba(var(--color-primary-rgb), 0.7)'
    },
    '50%': {
      transform: 'scale(1.05)',
      boxShadow: '0 0 0 8px rgba(var(--color-primary-rgb), 0)'
    },
    '100%': {
      transform: 'scale(1)',
      boxShadow: '0 0 0 0 rgba(var(--color-primary-rgb), 0)'
    }
  },
  '@keyframes blink': {
    '0%, 100%': { opacity: 1 },
    '50%': { opacity: 0.3 }
  },
  '@keyframes rotate': {
    from: { transform: 'rotate(0deg)' },
    to: { transform: 'rotate(360deg)' }
  }
}))

function StepChangeIndicator ({ id, stepId, stepName, status, duration, timestamp }) {
  const classes = useStyles()

  const getStepClass = () => {
    if (status === 'completed') return classes.stepCompleted
    if (status === 'active') return classes.stepActive
    return classes.stepPending
  }

  const getStepIcon = () => {
    if (status === 'completed') {
      return <Icon name='check' customSize={12} />
    }
    if (status === 'active') {
      return <Icon name='refresh' className={classes.loadingIcon} customSize={12} />
    }
    return <Icon name='radio_button_unchecked' customSize={12} />
  }

  const getStepNameClass = () => {
    if (status === 'completed') return `${classes.stepName} ${classes.stepNameCompleted}`
    if (status === 'active') return `${classes.stepName} ${classes.stepNameActive}`
    return classes.stepName
  }

  return (
    <div className='border-radius-md font-sans'>
      <div className={classes.stepList}>
        <div className={classes.step}>
          {/* Step icon */}
          <div className={classes.stepIconWrapper}>
            <div className={`${classes.stepIcon} ${getStepClass()}`}>
              {getStepIcon()}
            </div>
          </div>

          {/* Step content */}
          <div className={classes.stepContent}>
            <span className={getStepNameClass()}>
              {stepName}
              {status === 'active' ? (
                <span className={classes.activeIndicator} />
              ) : null}
            </span>
            {duration ? (
              <span className={classes.stepDuration}>
                {formatDuration(duration)}
              </span>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  )
}

StepChangeIndicator.propTypes = {
  id: PropTypes.string.isRequired,
  stepId: PropTypes.string.isRequired,
  stepName: PropTypes.string.isRequired,
  status: PropTypes.oneOf(['pending', 'active', 'completed']).isRequired,
  duration: PropTypes.number,
  timestamp: PropTypes.string
}

export default StepChangeIndicator
